(() => {
  'use strict';

  if (window.AuraFreeGift) return;

  const SELECTOR = '[data-free-gift]';
  const PROPERTY_NAME = '_free_gift';
  const PROPERTY_VALUE = 'aura_auto_gift_v2';
  const MANAGED_VALUES = new Set([
    'aura_auto_gift_v2',
    'aura_auto_gift_v1'
  ]);

  const ROOT =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  let lastCart = null;
  let lastHandledAt = 0;
  let syncTimer = null;
  let syncInFlight = false;
  let syncQueued = false;
  let variantTransactions = 0;

  function getElements() {
    return document.querySelectorAll(SELECTOR);
  }

  function getElement() {
    return document.querySelector(SELECTOR);
  }

  function getConfig() {
    const element = getElement();
    if (!element) return null;

    const target = Number(element.dataset.target || 0);
    const giftVariant = String(element.dataset.giftVariant || '');
    const giftAvailable = element.dataset.giftAvailable !== 'false';

    if (!Number.isFinite(target) || target <= 0 || !giftVariant) {
      return null;
    }

    return {
      target,
      giftVariant,
      giftAvailable
    };
  }

  function isCartObject(data) {
    return Boolean(
      data &&
      Array.isArray(data.items) &&
      data.items_subtotal_price !== undefined
    );
  }

  /* =========================================================
     FAST AUTHORITATIVE CART SNAPSHOT

     The cart-drawer Liquid embeds a compact JSON snapshot generated from
     Shopify's own cart object. Bundled section responses therefore already
     contain the full cart total and line keys we need. Reusing that snapshot
     avoids an extra blocking /cart.js request after product adds and checkout-
     order rebuilds. If a nested/parent line is detected, fall back to /cart.js
     because that relationship needs the richer Ajax cart shape.
     ========================================================= */
  function parseEmbeddedCartState(scope) {
    if (!scope || typeof scope.querySelector !== 'function') return null;

    const stateElement = scope.querySelector('[data-aura-cart-state]');
    if (!stateElement) return null;

    try {
      const cart = JSON.parse(stateElement.textContent || '{}');

      if (!isCartObject(cart)) return null;

      if (
        cart.items.some(
          (item) => item && item._aura_has_parent === true
        )
      ) {
        return null;
      }

      cart.items.forEach((item) => {
        if (item && Object.prototype.hasOwnProperty.call(item, '_aura_has_parent')) {
          delete item._aura_has_parent;
        }
      });

      return cart;
    } catch (error) {
      console.warn('[Aura Free Gift] Embedded cart state parse failed:', error);
      return null;
    }
  }

  function getCartFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;

    const html = sections['cart-drawer'];
    if (!html || typeof html !== 'string') return null;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parseEmbeddedCartState(parsed);
  }

  function getCartFromDOM() {
    return parseEmbeddedCartState(document);
  }

  function rememberCart(cart) {
    if (isCartObject(cart)) lastCart = cart;
  }

  function getManagedValue(item) {
    if (!item || !item.properties) return '';
    return String(item.properties[PROPERTY_NAME] || '');
  }

  function isManagedGift(item) {
    return MANAGED_VALUES.has(getManagedValue(item));
  }

  function isCurrentGift(item, config) {
    return Boolean(
      isManagedGift(item) &&
      String(item.variant_id) === String(config.giftVariant) &&
      getManagedValue(item) === PROPERTY_VALUE
    );
  }

  function getLegacyGiftCandidate(cart, config) {
    if (!isCartObject(cart)) return null;

    const matches = cart.items.filter((item) => {
      if (!item || isManagedGift(item)) return false;
      if (String(item.variant_id) !== String(config.giftVariant)) return false;
      if (Number(item.quantity) !== 1) return false;

      const properties = item.properties || {};
      if (Object.keys(properties).length) return false;

      const linePrice = Number(
        item.final_line_price != null
          ? item.final_line_price
          : item.line_price || 0
      );

      return linePrice === 0;
    });

    return matches.length === 1 ? matches[0] : null;
  }

  /* =========================================================
     QUALIFYING CART TOTAL

     The managed gift must NEVER qualify itself for the threshold.

     When the zero-price Cart Transform is active, the gift line already
     contributes 0 to cart.total_price, so this produces the same visible
     total. If the transform/price adjustment has not been applied yet, we
     subtract the managed gift's current final line price. This prevents the
     classic loop where a paid gift keeps the cart above the target and cannot
     be removed after a variant/price change.
     ========================================================= */
  function getQualifyingCartTotal(cart) {
    if (!isCartObject(cart)) return 0;

    const cartTotal = Number(cart.total_price || 0);
    const safeCartTotal = Number.isFinite(cartTotal) ? Math.max(0, cartTotal) : 0;

    const managedGiftTotal = cart.items.reduce((total, item) => {
      if (!isManagedGift(item)) return total;

      const linePrice = Number(
        item.final_line_price != null
          ? item.final_line_price
          : item.line_price || 0
      );

      return total + (Number.isFinite(linePrice) ? Math.max(0, linePrice) : 0);
    }, 0);

    return Math.max(0, safeCartTotal - managedGiftTotal);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'de-CH',
        {
          style: 'currency',
          currency: currency || 'CHF'
        }
      ).format(Number(cents || 0) / 100).replace(',', '.');
    } catch (error) {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  }

  function buildMessage(template, amount) {
    if (!template) return '';

    return String(template).replace(
      /\{\{\s*amount\s*\}\}/gi,
       '<strong>' + amount + '</strong>'
    );
  }

  function setProcessing(processing) {
    getElements().forEach((element) => {
      element.classList.toggle('is-processing', Boolean(processing));
      element.setAttribute('aria-busy', processing ? 'true' : 'false');
    });

    /*
     * Do not let the customer enter Checkout while a variant/gift cart
     * transaction is still being finalized. This is especially important
     * when the gift has just unlocked and the real Shopify line order is
     * being rebuilt for Checkout.
     */
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');

    if (checkoutButton) {
      if (processing) {
        if (!checkoutButton.hasAttribute('data-aura-was-disabled')) {
          checkoutButton.setAttribute(
            'data-aura-was-disabled',
            checkoutButton.disabled ? 'true' : 'false'
          );
        }

        checkoutButton.disabled = true;
        checkoutButton.setAttribute('aria-disabled', 'true');
      } else {
        const wasDisabled =
          checkoutButton.getAttribute('data-aura-was-disabled') === 'true';

        checkoutButton.removeAttribute('data-aura-was-disabled');

        if (!wasDisabled && (!lastCart || Number(lastCart.item_count || 0) > 0)) {
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute('aria-disabled');
        }
      }
    }
  }

  function paint(cart = lastCart) {
    // The theme's cart-variant.js performs a two-request transaction.
    // While that untouched transaction is running, any passive paint would
    // use the previous cart snapshot and could briefly show the wrong amount.
    if (variantTransactions > 0) return;

    const config = getConfig();
    const elements = getElements();

    if (!config || !isCartObject(cart) || !elements.length) return;

    rememberCart(cart);

    const cartTotal = getQualifyingCartTotal(cart);
    const percentage = Math.max(
      0,
      Math.min(100, (cartTotal / config.target) * 100)
    );

    const remaining = Math.max(0, config.target - cartTotal);
    const remainingMoney = formatMoney(remaining, cart.currency);
    const targetMoney = formatMoney(config.target, cart.currency);
    const unlocked = cartTotal >= config.target && config.giftAvailable;

    elements.forEach((element) => {
      const progress = element.querySelector(
        '.free-gift-cart-drawer__progress'
      );
      const fill = element.querySelector(
        '.free-gift-cart-drawer__progress-fill'
      );
      const message = element.querySelector('.free-gift-message');
      const success = element.querySelector(
        '.free-gift-cart-drawer__success'
      );

      if (progress) {
        progress.setAttribute(
          'aria-valuenow',
          String(Math.round(percentage))
        );
      }

      if (fill) {
        fill.style.transform = `scaleX(${percentage / 100})`;
      }

      if (unlocked) {
        element.classList.add('is-unlocked');

        if (success) {
          success.textContent = buildMessage(
            element.dataset.successMessage || '',
            targetMoney
          );
        }
      } else {
        element.classList.remove('is-unlocked');

        if (message) {
          let text = buildMessage(
            element.dataset.progressMessage || '',
            remainingMoney
          );

          if (!text) {
            text = buildMessage(
              element.dataset.amountMessage || '',
              remainingMoney
            );
          }

          message.innerHTML = text;
        }

        if (success) success.innerHTML = '';
      }
    });
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    if (!response.ok) {
      const message =
        data && (data.description || data.message)
          ? data.description || data.message
          : `Cart request failed (${response.status})`;

      throw new Error(message);
    }

    return data;
  }

  async function getCart() {
    const cart = await requestJSON(`${ROOT}cart.js`, {
      method: 'GET'
    });

    rememberCart(cart);
    return cart;
  }

  function normalizeSections(sections) {
    if (!Array.isArray(sections)) return [];

    return Array.from(
      new Set(
        sections.filter((section) => typeof section === 'string' && section)
      )
    );
  }

  function getSectionsUrl(value) {
    return value || window.location.pathname;
  }

  async function cartMutation(endpoint, payload, options = {}) {
    const body = { ...(payload || {}) };
    const sections = normalizeSections(options.sections);

    if (sections.length) {
      body.sections = sections;
      body.sections_url = getSectionsUrl(options.sectionsUrl);
    }

    return requestJSON(`${ROOT}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  function getManagedItems(cart, config) {
    const items = cart.items.filter(isManagedGift);
    const legacy = getLegacyGiftCandidate(cart, config);

    if (legacy) items.push(legacy);

    return items;
  }

  function getGiftIndex(cart, item) {
    if (!isCartObject(cart) || !item) return -1;

    if (item.key) {
      return cart.items.findIndex(
        (candidate) => String(candidate.key) === String(item.key)
      );
    }

    return cart.items.indexOf(item);
  }

  function isGiftAtCheckoutBottom(cart, item) {
    const index = getGiftIndex(cart, item);
    return index >= 0 && index === cart.items.length - 1;
  }

  function hasExactCheckoutGiftState(cart, config) {
    if (!isCartObject(cart)) return false;

    const managedItems = getManagedItems(cart, config);
    const currentItems = managedItems.filter((item) =>
      isCurrentGift(item, config)
    );
    const currentGift = currentItems.length === 1 ? currentItems[0] : null;

    return Boolean(
      managedItems.length === 1 &&
      currentGift &&
      Number(currentGift.quantity) === 1 &&
      isGiftAtCheckoutBottom(cart, currentGift)
    );
  }

  function cloneLineProperties(properties) {
    if (
      !properties ||
      typeof properties !== 'object' ||
      Array.isArray(properties)
    ) {
      return null;
    }

    const copy = {};

    Object.keys(properties).forEach((key) => {
      if (properties[key] !== undefined) {
        copy[key] = properties[key];
      }
    });

    return Object.keys(copy).length ? copy : null;
  }

  function getSellingPlanId(item) {
    const allocation = item && item.selling_plan_allocation;
    const sellingPlan = allocation && allocation.selling_plan;

    if (!sellingPlan || sellingPlan.id == null) return null;
    return sellingPlan.id;
  }

  function buildPaidLineInput(item, cart) {
    if (!item) return null;

    const variantId = Number(item.variant_id || item.id || 0);
    const quantity = Number(item.quantity || 0);

    if (!variantId || quantity <= 0) return null;

    const input = {
      id: variantId,
      quantity
    };

    const properties = cloneLineProperties(item.properties);
    if (properties) input.properties = properties;

    const sellingPlan = getSellingPlanId(item);
    if (sellingPlan != null && sellingPlan !== '') {
      input.selling_plan = sellingPlan;
    }

    /*
     * Preserve Shopify nested cart relationships when possible.
     * Ajax cart responses expose parent_relationship.parent_key; when the
     * parent and child are restored together Shopify accepts parent_id on
     * the child add input.
     */
    const parentKey =
      item.parent_relationship && item.parent_relationship.parent_key;

    if (parentKey && isCartObject(cart)) {
      const parent = cart.items.find(
        (candidate) => String(candidate.key) === String(parentKey)
      );

      if (parent && !isManagedGift(parent)) {
        const parentVariantId = Number(parent.variant_id || parent.id || 0);
        if (parentVariantId) input.parent_id = parentVariantId;
      }
    }

    return input;
  }

  function getPaidLineInputs(cart, config) {
    if (!isCartObject(cart)) return [];

    const managedItems = getManagedItems(cart, config);
    const managedKeys = new Set(
      managedItems
        .map((item) => item && item.key ? String(item.key) : '')
        .filter(Boolean)
    );

    return cart.items
      .filter((item) => {
        if (!item) return false;
        if (item.key && managedKeys.has(String(item.key))) return false;
        if (isManagedGift(item)) return false;
        return true;
      })
      .map((item) => buildPaidLineInput(item, cart))
      .filter(Boolean);
  }

  async function removeItems(items, options) {
    const updates = {};

    items.forEach((item) => {
      if (item && item.key) updates[String(item.key)] = 0;
    });

    if (!Object.keys(updates).length) return null;

    return cartMutation(
      'cart/update.js',
      { updates },
      options
    );
  }

  function buildGiftInput(config) {
    return {
      id: Number(config.giftVariant),
      quantity: 1,
      properties: {
        [PROPERTY_NAME]: PROPERTY_VALUE
      }
    };
  }

  async function addGift(config, options) {
    return cartMutation(
      'cart/add.js',
      {
        items: [buildGiftInput(config)]
      },
      options
    );
  }

  async function addRebuiltLinesFast(paidItems, config, options) {
    /*
     * Shopify commonly exposes newly added lines in LIFO cart order. The
     * paid snapshot is reversed in the request so the final visible paid
     * order remains stable while the gift becomes the oldest/last line.
     * The result is always verified from Shopify's returned cart-drawer
     * snapshot; if this session behaves differently, the proven sequential
     * fallback is used automatically.
     */
    const items = [
      buildGiftInput(config),
      ...paidItems.slice().reverse()
    ];

    return cartMutation(
      'cart/add.js',
      { items },
      options
    );
  }

  async function addPaidLines(items, options) {
    if (!Array.isArray(items) || !items.length) return null;

    return cartMutation(
      'cart/add.js',
      { items },
      options
    );
  }

  async function clearCartItems() {
    return cartMutation(
      'cart/clear.js',
      {},
      { sections: [] }
    );
  }

  function copySectionsOntoCart(cart, sections) {
    if (isCartObject(cart) && sections) {
      cart.sections = sections;
    }
    return cart;
  }

  function getRebuildOptions(options = {}) {
    const sections = normalizeSections(options.sections);

    /*
     * Shopify bundled section rendering supports up to five sections.
     * Add cart-drawer when there is room so its embedded state can replace
     * a blocking /cart.js GET after the rebuild.
     */
    if (!sections.includes('cart-drawer') && sections.length < 5) {
      sections.push('cart-drawer');
    }

    return {
      ...options,
      sections
    };
  }

  function getAddedItems(response) {
    if (!response) return [];

    if (Array.isArray(response.items)) {
      return response.items.filter(Boolean);
    }

    if (response.key || response.variant_id || response.id) {
      return [response];
    }

    return [];
  }

  function buildFastRebuiltCart(sourceCart, paidResponse, giftResponse) {
    if (!isCartObject(sourceCart)) return null;

    const paidItems = getAddedItems(paidResponse);
    const giftItems = getAddedItems(giftResponse);
    const giftItem = giftItems[0] || null;

    if (!giftItem) return null;

    const items = [...paidItems, giftItem];
    const itemCount = items.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    );

    return {
      ...sourceCart,
      items,
      item_count: itemCount
    };
  }

  /* =========================================================
     CHECKOUT-SAFE GIFT ORDER

     Shopify Checkout does not use the drawer's CSS `order` rule. It uses
     the real cart-line sequence. A newly-added gift therefore appears at
     the top of Checkout.

     To make the gift truly LAST in cart.items / Checkout, rebuild only
     when the gift is first unlocked (or an old cart has the wrong order):

       snapshot paid lines
       -> clear cart items
       -> add gift FIRST
       -> restore paid lines AFTER the gift
       -> render only the final cart

     Shopify prepends the later paid add to the cart sequence, leaving the
     earlier gift as the last line. cart/clear.js keeps cart attributes and
     cart note intact. Line properties and selling plans are restored.
     ========================================================= */
  async function rebuildForCheckoutOrder(cart, config, options = {}) {
    const paidInputs = getPaidLineInputs(cart, config);
    const rebuildOptions = getRebuildOptions(options);
    let latestSections = options.existingSections || cart.sections || null;
    let cleared = false;

    try {
      await clearCartItems();
      cleared = true;

      let finalCart = null;

      if (paidInputs.length) {
        /* =====================================================
           TURBO PATH -- 2 mutations total

           1) clear
           2) add gift + all paid lines together

           The returned cart-drawer embeds Shopify's real cart order. If the
           gift is verified as the final line, we are done immediately.
           ===================================================== */
        const bulkResponse = await addRebuiltLinesFast(
          paidInputs,
          config,
          rebuildOptions
        );

        if (bulkResponse && bulkResponse.sections) {
          latestSections = bulkResponse.sections;
        }

        finalCart = getCartFromSections(latestSections);

        /*
         * The current theme always returns cart-drawer here. This fallback
         * protects compatibility if an app strips bundled sections.
         */
        if (!isCartObject(finalCart)) {
          finalCart = await getCart();
        }

        if (!hasExactCheckoutGiftState(finalCart, config)) {
          /* ===================================================
             ORDER FALLBACK -- proven V5 sequence

             Some Shopify sessions can expose a different insertion order.
             Never trade checkout correctness for speed: if the turbo result
             is not gift-last, rebuild with the already proven sequence.
             =================================================== */
          await clearCartItems();

          const giftResponse = await addGift(
            config,
            { sections: [] }
          );

          const paidResponse = await addPaidLines(
            paidInputs,
            rebuildOptions
          );

          if (paidResponse && paidResponse.sections) {
            latestSections = paidResponse.sections;
          }

          finalCart =
            getCartFromSections(latestSections) ||
            buildFastRebuiltCart(cart, paidResponse, giftResponse);

          if (!isCartObject(finalCart)) {
            finalCart = await getCart();
          }
        }
      } else {
        /* Defensive fallback; a qualifying cart normally has paid lines. */
        const giftResponse = await addGift(
          config,
          rebuildOptions
        );

        if (giftResponse && giftResponse.sections) {
          latestSections = giftResponse.sections;
        }

        finalCart =
          getCartFromSections(latestSections) ||
          buildFastRebuiltCart(cart, null, giftResponse);

        if (!isCartObject(finalCart)) {
          finalCart = await getCart();
        }
      }

      copySectionsOntoCart(finalCart, latestSections);
      rememberCart(finalCart);
      paint(finalCart);

      return {
        cart: finalCart,
        changed: true,
        sections: latestSections
      };
    } catch (error) {
      console.error('[Aura Free Gift] Checkout-order rebuild failed:', error);

      /*
       * Never intentionally leave the customer with a gift-only/empty cart.
       * Best-effort recovery restores the paid snapshot without the gift.
       * This is an error-only path, so correctness is preferred over speed.
       */
      if (cleared && paidInputs.length) {
        try {
          await clearCartItems();
          const restoreResponse = await addPaidLines(
            paidInputs,
            rebuildOptions
          );

          if (restoreResponse && restoreResponse.sections) {
            latestSections = restoreResponse.sections;
          }

          const restoredCart =
            getCartFromSections(latestSections) ||
            await getCart();

          copySectionsOntoCart(restoredCart, latestSections);
          rememberCart(restoredCart);
          paint(restoredCart);
        } catch (restoreError) {
          console.error(
            '[Aura Free Gift] Paid-cart recovery failed:',
            restoreError
          );
        }
      }

      throw error;
    }
  }

  async function reconcile(cart, options = {}) {
    const config = getConfig();

    if (!config || !isCartObject(cart)) {
      return {
        cart,
        changed: false,
        sections: options.existingSections || null
      };
    }

    rememberCart(cart);
    paint(cart);

    const cartTotal = getQualifyingCartTotal(cart);
    const shouldHaveGift = cartTotal >= config.target && config.giftAvailable;
    const managedItems = getManagedItems(cart, config);
    const exactState = hasExactCheckoutGiftState(cart, config);

    if (shouldHaveGift && exactState) {
      return {
        cart,
        changed: false,
        sections: options.existingSections || cart.sections || null
      };
    }

    if (!shouldHaveGift && !managedItems.length) {
      return {
        cart,
        changed: false,
        sections: options.existingSections || cart.sections || null
      };
    }

    /*
     * If the gift should exist but is missing, duplicated, wrong quantity,
     * old version, wrong variant, OR physically above a paid line, do one
     * checkout-safe rebuild. This fixes Cart Drawer and Checkout together.
     */
    if (shouldHaveGift) {
      return rebuildForCheckoutOrder(cart, config, options);
    }

    /* Threshold dropped: remove only managed/legacy free-gift lines. */
    const removeResponse = await removeItems(managedItems, options);
    let finalCart = cart;
    let latestSections = options.existingSections || cart.sections || null;

    if (removeResponse) {
      if (removeResponse.sections) latestSections = removeResponse.sections;

      if (isCartObject(removeResponse)) {
        finalCart = removeResponse;
      } else if (options.needFullCart) {
        finalCart = await getCart();
      }
    }

    copySectionsOntoCart(finalCart, latestSections);
    rememberCart(finalCart);
    paint(finalCart);

    return {
      cart: finalCart,
      changed: Boolean(removeResponse),
      sections: latestSections
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function applySections(sections) {
    if (!sections || typeof sections !== 'object') return false;

    const sectionCart = getCartFromSections(sections);
    if (sectionCart) rememberCart(sectionCart);

    let changed = false;

    if (sections['cart-drawer']) {
      const currentDrawer = document.querySelector('cart-drawer');

      if (currentDrawer) {
        const parsed = parseHTML(sections['cart-drawer']);
        const newDrawer = parsed.querySelector('cart-drawer');
        const currentInner = currentDrawer.querySelector('.drawer__inner');
        const newInner = newDrawer && newDrawer.querySelector('.drawer__inner');

        if (newDrawer && currentInner && newInner) {
          const scrollTop = currentInner.scrollTop;
          const wasOpen = currentDrawer.classList.contains('active');

          currentInner.replaceWith(newInner);
          currentDrawer.classList.toggle(
            'is-empty',
            newDrawer.classList.contains('is-empty')
          );

          if (wasOpen) currentDrawer.classList.add('active');

          const replacedInner = currentDrawer.querySelector('.drawer__inner');
          if (replacedInner) replacedInner.scrollTop = scrollTop;

          changed = true;
        }
      }
    }

    if (sections['cart-icon-bubble']) {
      const currentBubble = document.querySelector('#cart-icon-bubble');

      if (currentBubble) {
        const parsed = parseHTML(sections['cart-icon-bubble']);
        const newBubble = parsed.querySelector('#cart-icon-bubble');

        if (newBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
          changed = true;
        }
      }
    }

    if (syncInFlight || variantTransactions > 0) {
      setProcessing(true);
    }

    window.requestAnimationFrame(() => paint(sectionCart || lastCart));
    return changed;
  }

  function markHandled() {
    lastHandledAt = Date.now();
  }

  async function afterProductAdd(response, sections = []) {
    if (!getConfig()) return response;

    markHandled();
    setProcessing(true);

    try {
      /*
       * Product-form already requested cart-drawer as a bundled section.
       * Read its embedded authoritative cart state first; only fall back to
       * /cart.js when a third-party theme/app removed that section.
       */
      const sectionCart = getCartFromSections(
        response && response.sections ? response.sections : null
      );
      const cart = sectionCart || await getCart();

      const result = await reconcile(cart, {
        sections,
        sectionsUrl: window.location.pathname,
        existingSections: response && response.sections
          ? response.sections
          : null,
        needFullCart: false
      });

      if (response && result.sections) {
        response.sections = result.sections;
      }

      rememberCart(result.cart || cart);
      return response;
    } catch (error) {
      console.error('[Aura Free Gift] Product add sync failed:', error);
      return response;
    } finally {
      setProcessing(false);
    }
  }

  async function afterCartChange(cart, sections = []) {
    if (!getConfig()) return cart;

    markHandled();
    setProcessing(true);

    try {
      const result = await reconcile(cart, {
        sections,
        sectionsUrl: window.location.pathname,
        existingSections: cart && cart.sections ? cart.sections : null,
        needFullCart: true
      });

      const finalCart = result.cart || cart;
      copySectionsOntoCart(finalCart, result.sections);
      rememberCart(finalCart);
      return finalCart;
    } catch (error) {
      console.error('[Aura Free Gift] Cart change sync failed:', error);
      return cart;
    } finally {
      setProcessing(false);
    }
  }

  async function afterVariantChange(
    cart,
    sections = ['cart-drawer', 'cart-icon-bubble']
  ) {
    if (!getConfig()) return cart;

    markHandled();

    /*
     * cart-variant.js passes Shopify's FINAL cart response here, after the
     * new variant has been added and the old line has been removed.
     * This function does not render the drawer itself. It first makes the
     * gift state correct, then returns the newest bundled sections so the
     * variant switcher can paint one final, synchronized drawer state.
     */
    const ownsProcessingState = variantTransactions === 0;
    if (ownsProcessingState) setProcessing(true);

    try {
      const requestedSections = normalizeSections(sections);
      const existingSections =
        cart && cart.sections ? cart.sections : null;

      const result = await reconcile(cart, {
        sections: requestedSections,
        sectionsUrl: window.location.pathname,
        existingSections,
        needFullCart: false
      });

      const finalCart = result.cart || cart;
      copySectionsOntoCart(
        finalCart,
        result.sections || existingSections
      );

      rememberCart(finalCart);
      return finalCart;
    } catch (error) {
      console.error('[Aura Free Gift] Variant change sync failed:', error);
      return cart;
    } finally {
      if (ownsProcessingState) setProcessing(false);
    }
  }

  async function runSync(seedCart = null) {
    if (!getConfig()) return;

    if (variantTransactions > 0) {
      syncQueued = true;
      return;
    }

    if (syncInFlight) {
      syncQueued = true;
      return;
    }

    syncInFlight = true;
    let nextCart = isCartObject(seedCart) ? seedCart : null;

    try {
      do {
        syncQueued = false;
        setProcessing(true);

        try {
          /*
           * On initial page load the Liquid drawer already embeds the current
           * cart state, so use it without a network read. Subsequent queued
           * syncs fall back to /cart.js only when no authoritative response is
           * available from the action that changed the cart.
           */
          const cart = nextCart || await getCart();
          nextCart = null;

          const result = await reconcile(cart, {
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sectionsUrl: window.location.pathname,
            needFullCart: false
          });

          if (result.changed && result.sections) {
            applySections(result.sections);
          }

          rememberCart(result.cart || cart);
          paint(result.cart || cart);
        } catch (error) {
          console.error('[Aura Free Gift] Sync failed:', error);
        } finally {
          setProcessing(false);
        }
      } while (syncQueued);
    } finally {
      syncInFlight = false;
    }
  }

  function scheduleSync(delay = 80) {
    if (variantTransactions > 0) {
      syncQueued = true;
      return;
    }

    clearTimeout(syncTimer);
    syncTimer = setTimeout(runSync, delay);
  }


  /* =========================================================
     DIRECT CART-VARIANT TRANSACTION LOCK

     cart-variant.js integrates directly with Aura Free Gift. While its atomic
     variant replacement/final gift reconciliation is running, background
     free-gift sync is paused so independent cart mutations cannot race it.
     ========================================================= */

  async function beginVariantTransaction() {
    variantTransactions += 1;

    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }

    setProcessing(true);

    /*
     * If an initial/background Aura reconciliation was already in flight,
     * let it finish before cart-variant.js starts changing product lines.
     * This prevents two independent cart mutations from racing each other.
     */
    const startedAt = Date.now();
    while (syncInFlight && Date.now() - startedAt < 5000) {
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
  }

  function endVariantTransaction(options = {}) {
    variantTransactions = Math.max(0, variantTransactions - 1);

    if (variantTransactions > 0) return;

    setProcessing(false);

    if (options.synced) {
      /* The caller already reconciled the authoritative final cart. */
      syncQueued = false;
      return;
    }

    /* On a failed/incomplete transaction, repair from Shopify once. */
    if (syncQueued || options.sync !== false) {
      syncQueued = false;
      scheduleSync(0);
    }
  }

  function installPubSubFallback() {
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (Date.now() - lastHandledAt < 250) return;

      if (event && isCartObject(event.cartData)) {
        rememberCart(event.cartData);
        paint(event.cartData);
      }

      scheduleSync(90);
    });
  }

  ['cart:updated', 'cart:refresh', 'cartUpdated'].forEach((eventName) => {
    document.addEventListener(eventName, () => {
      if (Date.now() - lastHandledAt < 250) return;
      scheduleSync(90);
    });
  });

  const observer = new MutationObserver((mutations) => {
    if (!lastCart) return;

    if (variantTransactions > 0) return;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (
          node.matches(SELECTOR) ||
          node.querySelector(SELECTOR)
        ) {
          window.requestAnimationFrame(() => paint(lastCart));
          return;
        }
      }
    }
  });

  window.AuraFreeGift = Object.freeze({
    afterProductAdd,
    afterCartChange,
    afterVariantChange,
    applySections,
    beginVariantTransaction,
    endVariantTransaction,
    paint,
    scheduleSync,
    setProcessing,
    sync: runSync
  });

  function start() {
    installPubSubFallback();

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    runSync(getCartFromDOM());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', (event) => {
    /* Normal initial load already used the embedded Liquid cart snapshot. */
    if (event.persisted) scheduleSync(0);
  });
})();
